/*
 * Schedule: Month (a grid of weeks, every visit on its day) as the old Steward had it (owner, 2026-10-28), and List, which
 * looks like the phone's (owner, 2026-10-30): the week as a strip of days, then a column per person for the day picked,
 * each visit a card with the job, client, time and address (DayBoard in SchedulePhone.tsx). Drag a card onto a day in the
 * strip to move it. On the month each visit shows its time, the client and the job,
 * the crew's initials in circles and a color for the kind of job (repeating, one-time, commercial, repeating commercial),
 * and is grayed with a check and struck through once done. Click a visit for its card beside it (VisitPeek). Drag a visit
 * to another day; the move shows at once and goes back if the server says no, with Undo after. Right-click a visit for
 * Open, Edit schedule, Complete and Skip. A month day's number opens
 * that week as a list. Visits sort by time, and skipped and canceled ones stay hidden (and uncounted) unless asked for,
 * like the old Steward (owner, 2026-10-29).
 *
 * With routes on (Settings, Schedule; on by default for lawn) there is a third view, Route: one day with each route's stops
 * in driving order and a map (RouteDay.tsx). On the month and list a route shows as one colored line per day with its count,
 * as the old Steward did; click it for that day's route, drag it to move the whole route that day only.
 */
import { Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addDays,
  dayVisits,
  daysBetween,
  isOffSchedule,
  mondayOf,
  type RouteRow,
  type ScheduleAnswer,
  type VisitCard,
  type WalkthroughRow,
} from "@steward/shared";
import { useEffect, useState, type DragEvent, type MouseEvent, type ReactNode } from "react";
import {
  useCompleteVisit,
  useMoveVisit,
  useReopenVisit,
  useRoutes,
  useVisits,
  useVisitStatus,
  visitsKey,
  type VisitsQuery,
} from "../api/hooks";
import { api } from "../api/client";
import { useSession } from "../app/session";
import { useWordColor } from "../components/wordColor";
import { dayDate, money, monthName, plural, shortDate, timeRange, todayIn, weekdayName } from "../components/format";
import { Icon } from "../components/Icon";
import { dragAutoScroll } from "../components/autoscroll";
import { useToast } from "../components/overlays";
import { Avatar, Button, Chip, IconButton, LoadError, Page, Skeleton } from "../components/parts";
import { VisitPeek } from "../components/VisitPeek";
import { VisitDrawer } from "../components/VisitDrawer";
import { usePhone } from "../components/usePhone";
import { errText, ScheduleSheet } from "../components/work";
import { MoveRouteModal, RouteDay, routeColor, useRoutesOn, WEEKDAYS } from "./RouteDay";
import { busyPeople, DayBoard, SchedulePhone } from "./SchedulePhone";
import { SettingsButton } from "./settings/Feature";

type View = "list" | "month" | "route";
const NOBODY = "none";

/** The kind of job, for its color: repeating, one-time, commercial (one-time) or repeating commercial. */
type Kind = "rec" | "one" | "com" | "rcom";
const kindOf = (v: VisitCard): Kind => (v.commercial ? (v.oneTime ? "com" : "rcom") : v.oneTime ? "one" : "rec");
const KIND_WORDS: [Kind, string][] = [
  ["rec", "Repeating"],
  ["one", "One-time"],
  ["com", "Commercial"],
  ["rcom", "Repeating commercial"],
];

/** The "N more" line's height, as in app.css (.sched-more). It sits over the bottom of a month day. */
const MORE = 20;

/**
 * A month day keeps its size as the month fills the window. Every line is there; what does not fit scrolls, and an
 * "N more" line counts what is still below. Clicking it scrolls down to the rest.
 */
function DayStack({ lines }: { lines: ReactNode[] }) {
  const [el, setEl] = useState<HTMLDivElement | null>(null);
  const [below, setBelow] = useState(0);
  const [scrolls, setScrolls] = useState(false);
  useEffect(() => {
    if (!el) return;
    const count = () => {
      const fits = el.scrollHeight <= el.clientHeight + 1;
      const bottom = el.scrollTop + el.clientHeight - (fits ? 0 : MORE);
      let n = 0;
      for (const c of el.children) {
        const line = c as HTMLElement;
        if (!line.classList.contains("sched-more") && line.offsetTop + line.offsetHeight > bottom + 1) n++;
      }
      setScrolls(!fits);
      setBelow(fits ? 0 : n);
    };
    const watch = new ResizeObserver(count);
    watch.observe(el);
    el.addEventListener("scroll", count, { passive: true });
    count();
    return () => {
      watch.disconnect();
      el.removeEventListener("scroll", count);
    };
  }, [el, lines.length]);
  return (
    <div ref={setEl} className={`sched-stack${scrolls ? " scrolls" : ""}`}>
      {lines}
      {scrolls ? (
        <button
          type="button"
          className="sched-more"
          style={below ? undefined : { visibility: "hidden" }}
          tabIndex={below ? 0 : -1}
          onClick={() => el?.scrollBy({ top: el.clientHeight - MORE, behavior: "smooth" })}
        >
          {below} more
        </button>
      ) : null}
    </div>
  );
}

/** "09:00" is 9 AM, "13:30" is 1:30 PM. */
function shortClock(hhmm: string): string {
  const [h = "0", m = "00"] = hhmm.split(":");
  const hour = Number(h);
  return `${String(((hour + 11) % 12) + 1)}${m === "00" ? "" : `:${m}`} ${hour < 12 ? "AM" : "PM"}`;
}

/** The 1st of the month n months from a month's 1st ("2026-11-01" and -1 is "2026-10-01"). */
function shiftMonth(first: string, n: number): string {
  const total = Number(first.slice(0, 4)) * 12 + Number(first.slice(5, 7)) - 1 + n;
  return `${String(Math.floor(total / 12))}-${String((total % 12) + 1).padStart(2, "0")}-01`;
}

const ROUTE_KEY = "steward.schedule.route";

/** Did this phone last leave the Schedule on the Route view? Lawn companies start there (they run on routes). */
export function readRouteFirst(trade: string): boolean {
  try {
    const v = localStorage.getItem(ROUTE_KEY);
    return v === null ? trade === "lawn" : v === "1";
  } catch {
    return trade === "lawn";
  }
}

export function saveRouteFirst(on: boolean) {
  try {
    localStorage.setItem(ROUTE_KEY, on ? "1" : "0");
  } catch {
    // Kept until the page closes.
  }
}

/**
 * Phones get the day board with a column per person (SchedulePhone), and with routes on a Route button that opens the
 * picked day's route; bigger screens get Route, List and Month. The phone opens on whichever of the two it was left on, so
 * the Schedule tab is one tap to today's route (owner, 2026-10-30: the GroundsMen work from it all day).
 */
export function Schedule() {
  const phone = usePhone();
  const routing = useRoutesOn();
  const { company } = useSession();
  const today = todayIn(company.timezone);
  const [routeDay, setRouteDay] = useState<string | null>(() => (readRouteFirst(company.trade) ? today : null));
  if (!phone) return <ScheduleBoard />;
  const openRoute = (d: string) => {
    saveRouteFirst(true);
    setRouteDay(d);
  };
  if (routing && routeDay)
    return (
      <Page title={dayDate(routeDay)} subtitle="Route">
        <div className="row tight">
          <Button
            type="secondary"
            size="small"
            icon="schedule"
            onClick={() => {
              saveRouteFirst(false);
              setRouteDay(null);
            }}
          >
            Day board
          </Button>
          <IconButton icon="arrowLeft" label="Day before" onClick={() => setRouteDay(addDays(routeDay, -1))} />
          {routeDay === today ? null : (
            <Button type="tertiary" size="small" onClick={() => setRouteDay(today)}>
              Today
            </Button>
          )}
          <IconButton icon="arrowRight" label="Next day" onClick={() => setRouteDay(addDays(routeDay, 1))} />
        </div>
        <RouteDay date={routeDay} onDate={setRouteDay} />
      </Page>
    );
  return <SchedulePhone {...(routing ? { onRoute: openRoute } : {})} />;
}

function ScheduleBoard() {
  const { company, can } = useSession();
  const wordColor = useWordColor();
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const today = todayIn(company.timezone);
  const [view, setView] = useState<View>("month");
  const [anchor, setAnchor] = useState(today);
  const [person, setPerson] = useState<string | null>(null);
  const [menu, setMenu] = useState<{ v: VisitCard; x: number; y: number } | null>(null);
  const [editing, setEditing] = useState<VisitCard | null>(null);
  /** The visit whose card is open beside it (click a visit), and where it sits. */
  const [peek, setPeek] = useState<{ v: VisitCard; at: DOMRect } | null>(null);
  /** The visit's card pulled out on the right (Details on the popover). */
  const [drawer, setDrawer] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const [showOff, setShowOff] = useState(false);
  const routing = useRoutesOn();
  const routes = useRoutes();
  const routeList = routing ? (routes.data?.routes ?? []) : [];
  const routeById = new Map(routeList.map((r, i) => [r.id, { r, color: routeColor(r, i) }]));
  const [routeMove, setRouteMove] = useState<{ route: RouteRow; from: string; to: string } | null>(null);
  const shownView: View = view === "route" && !routing ? "month" : view;
  const monthStart = `${anchor.slice(0, 8)}01`;
  const monthEnd = addDays(shiftMonth(monthStart, 1), -1);
  const from = shownView === "route" ? anchor : shownView === "list" ? mondayOf(anchor) : mondayOf(monthStart);
  const to = shownView === "route" ? anchor : shownView === "list" ? addDays(from, 6) : addDays(mondayOf(monthEnd), 6);
  const query: VisitsQuery = {
    from,
    to,
    ...(shownView !== "route" && person && person !== NOBODY ? { personId: person } : {}),
    ...(shownView !== "route" && person === NOBODY ? { unassigned: true } : {}),
  };
  const key = visitsKey(company.id, query);
  const visits = useVisits(company.id, query);
  const walks = useQuery({
    queryKey: ["walkthroughs", "days", company.id, from, to],
    queryFn: () => api<{ walkthroughs: WalkthroughRow[] }>("GET", `walkthroughs?from=${from}&to=${to}`),
    enabled: shownView !== "route" && can("quote.read"),
  });
  const move = useMoveVisit();
  const complete = useCompleteVisit();
  const reopen = useReopenVisit();
  const status = useVisitStatus();
  const data = visits.data;
  const list = data?.visits ?? [];
  const people = data?.people ?? [];
  const movable = can("schedule.update");
  const step = (dir: 1 | -1) =>
    setAnchor(shownView === "month" ? shiftMonth(monthStart, dir) : addDays(anchor, dir * (shownView === "route" ? 1 : 7)));
  const openDay = (d: string) => {
    setAnchor(d);
    setView("list");
  };

  useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(null);
    window.addEventListener("click", close);
    window.addEventListener("keydown", close);
    window.addEventListener("scroll", close, true);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("keydown", close);
      window.removeEventListener("scroll", close, true);
    };
  }, [menu]);

  /** Shows a change on the board at once; puts it back if the server says no. */
  const optimistic = (ref: string, change: (v: VisitCard) => VisitCard) => {
    const before = qc.getQueryData<ScheduleAnswer>(key);
    qc.setQueryData<ScheduleAnswer>(key, (old) =>
      old ? { ...old, visits: old.visits.map((v) => (v.ref === ref ? change(v) : v)) } : old,
    );
    return () => qc.setQueryData(key, before);
  };
  const moveTo = (v: VisitCard, date: string) => {
    if (date === v.date) return;
    if (v.status === "done") {
      toast("This visit is done. Reopen it to move it.", { kind: "error" });
      return;
    }
    const rollback = optimistic(v.ref, (x) => ({ ...x, date, moved: date !== x.originalDate }));
    move.mutate(
      { ref: v.ref, date },
      {
        onSuccess: (d) =>
          toast(`Moved to ${dayDate(date)}`, {
            undo: () => move.mutate({ ref: d.ref, date: v.date }, { onError: (e) => toast(errText(e), { kind: "error" }) }),
          }),
        onError: (e) => {
          rollback();
          toast(errText(e, "That move didn't save. It is back where it was."), { kind: "error" });
        },
      },
    );
  };
  const drag = (v: VisitCard) => ({
    draggable: movable && v.status !== "done",
    onDragStart: (e: DragEvent) => {
      e.dataTransfer.setData("text/plain", v.ref);
      e.dataTransfer.effectAllowed = "move";
      dragAutoScroll();
    },
  });
  const drop = (target: string, act: (v: VisitCard) => void) => ({
    onDragOver: (e: DragEvent) => {
      if (!movable) return;
      e.preventDefault();
      setOver(target);
    },
    onDragLeave: () => setOver((o) => (o === target ? null : o)),
    onDrop: (e: DragEvent) => {
      e.preventDefault();
      setOver(null);
      const got = e.dataTransfer.getData("text/plain");
      // A whole route on one day: "route:<id>:<day>". It moves that day only.
      if (got.startsWith("route:")) {
        const [, id = "", day = ""] = got.split(":");
        const r = routeById.get(id)?.r;
        if (r && day !== target) setRouteMove({ route: r, from: day, to: target });
        return;
      }
      const v = list.find((x) => x.ref === got);
      if (v) act(v);
    },
  });
  const open = (v: VisitCard) => void navigate({ to: "/visits/$ref", params: { ref: v.ref } });
  const context = (v: VisitCard) => (e: MouseEvent) => {
    e.preventDefault();
    setMenu({ v, x: Math.min(e.clientX, window.innerWidth - 220), y: Math.min(e.clientY, window.innerHeight - 200) });
  };
  const line = (v: VisitCard) => {
    const done = v.status === "done";
    const off = v.status === "skipped" || v.status === "cancelled";
    return (
      <button
        type="button"
        key={v.ref}
        style={wordColor(v)}
        className={`sched-line kind-${kindOf(v)}${done ? " done" : ""}${off ? " off" : ""}${v.date < today && v.status === "scheduled" ? " late" : ""}${peek?.v.ref === v.ref ? " peek-on" : ""}`}
        onClick={(e) => setPeek({ v, at: e.currentTarget.getBoundingClientRect() })}
        onContextMenu={context(v)}
        {...drag(v)}
        title={`${v.clientName}, ${v.jobName}, ${timeRange(v.startTime, v.endTime)}${v.moved ? `, moved from ${shortDate(v.originalDate)}` : ""}${v.unconfirmed ? ", not confirmed" : ""}`}
      >
        {done ? <Icon name="check" size={12} label="Done" /> : null}
        <span className="txt">
          {v.startTime ? <b className="num">{shortClock(v.startTime)}</b> : null}
          <span className="who">{v.clientName}</span>
          <span className="job">{v.jobName}</span>
        </span>
        {v.moved ? <Icon name="repeat" size={11} label="Moved" /> : null}
        {v.unconfirmed ? <Icon name="warning" size={11} label="Not confirmed" /> : null}
        <span className="inits">
          {v.crew.length ? (
            v.crew.map((c) => <Avatar key={c.id} name={c.name} person={c} size="small" title={c.name} />)
          ) : (
            <span className="init none" title="No crew yet">
              ?
            </span>
          )}
        </span>
      </button>
    );
  };
  const byDay = (d: string) => dayVisits(list, d, showOff);
  /** A walkthrough planned on a day (Requests, Walkthrough), outlined, before the visits. Click opens its request. */
  const walkLines = (d: string) =>
    (walks.data?.walkthroughs ?? [])
      .filter((w) => w.day === d)
      .map((w) => (
        <button
          type="button"
          key={`walk-${w.id}`}
          className="sched-line sched-walk"
          onClick={() =>
            w.leadId
              ? void navigate({ to: "/requests/$id", params: { id: w.leadId } })
              : w.clientId
                ? void navigate({ to: "/clients/$id", params: { id: w.clientId } })
                : undefined
          }
          title={`Walkthrough${w.forName ? ` at ${w.forName}` : ""}, ${timeRange(w.time, null)}${w.who ? `, ${w.who} goes` : ""}`}
        >
          <span className="txt">
            {w.time ? <b className="num">{shortClock(w.time)}</b> : null}
            <span className="who">Walkthrough</span> <span className="job">{w.forName ?? ""}</span>
          </span>
        </button>
      ));
  /** A day's visits, with each route's stops folded into one colored line (routes on). */
  const items = (d: string) => {
    const here = byDay(d);
    if (!routing) return [...walkLines(d), ...here.map(line)];
    const onRoute = new Map<string, VisitCard[]>();
    const rest: VisitCard[] = [];
    for (const v of here)
      if (v.routeId && routeById.has(v.routeId)) onRoute.set(v.routeId, [...(onRoute.get(v.routeId) ?? []), v]);
      else rest.push(v);
    return [
      ...[...onRoute.entries()].map(([id, vs]) => {
        const { r, color } = routeById.get(id)!;
        const done = vs.filter((v) => v.status === "done").length;
        const all = done === vs.length;
        return (
          <button
            type="button"
            key={`route-${id}`}
            className={`sched-line sched-route${all ? " done" : ""}`}
            style={{ ["--rc" as string]: color }}
            draggable={movable && !all}
            onDragStart={(e) => {
              e.dataTransfer.setData("text/plain", `route:${id}:${d}`);
              e.dataTransfer.effectAllowed = "move";
              dragAutoScroll();
            }}
            onClick={() => {
              setAnchor(d);
              setView("route");
            }}
            title={`${r.name}, ${plural(vs.length, "stop")}, ${String(done)} done. Click to see the stops. Drag to move it this day only.`}
          >
            {all ? <Icon name="check" size={12} label="Done" /> : <span className="kdot" aria-hidden />}
            <span className="txt">
              <span className="who">{r.name}</span>
            </span>
            <span className="num rcount">{vs.length}</span>
          </button>
        );
      }),
      ...walkLines(d),
      ...rest.map(line),
    ];
  };
  const shown = showOff ? list : list.filter((v) => !isOffSchedule(v.status));
  const days = Array.from({ length: daysBetween(from, to) + 1 }, (_, i) => addDays(from, i));
  const title = shownView === "month" ? monthName(monthStart) : dayDate(anchor);
  const add = (d: string) =>
    can("job.update") ? (
      <Link to="/jobs/new" className="sched-add" title={`Add a job on ${dayDate(d)}`} aria-label={`Add a job on ${dayDate(d)}`}>
        <Icon name="plus" size={14} />
      </Link>
    ) : null;
  const value = list.filter((v) => !isOffSchedule(v.status)).reduce((s, v) => s + v.totalCents, 0);

  return (
    <Page
      fill
      title="Schedule"
      subtitle={`${plural(shown.length, "visit")} · ${money(value)}`}
      secondaryAction={<Link to="/schedule/tools">Time off and capacity</Link>}
      moreActions={<SettingsButton to="/schedule/settings">Schedule settings</SettingsButton>}
    >
      <div className="sched-bar">
        <div className="row tight">
          <IconButton
            icon="chevronLeft"
            label={shownView === "month" ? "Month before" : shownView === "route" ? "Day before" : "Week before"}
            onClick={() => step(-1)}
          />
          <Button
            type="secondary"
            size="small"
            disabled={shownView === "month" ? anchor.slice(0, 7) === today.slice(0, 7) : anchor === today}
            onClick={() => setAnchor(today)}
          >
            Today
          </Button>
          <IconButton
            icon="chevronRight"
            label={shownView === "month" ? "Next month" : shownView === "route" ? "Next day" : "Next week"}
            onClick={() => step(1)}
          />
          <h2 className="sched-title">{title}</h2>
        </div>
        {shownView === "route" ? null : (
          <div className="chips person-filter" role="group" aria-label="Show visits for">
            <Chip pressed={person === null} onClick={() => setPerson(null)}>
              Everyone
            </Chip>
            {people.map((p) => (
              <Chip key={p.id} pressed={person === p.id} onClick={() => setPerson(person === p.id ? null : p.id)}>
                <Avatar name={p.name} person={p} size="small" />
                {p.name}
              </Chip>
            ))}
            <Chip pressed={person === NOBODY} onClick={() => setPerson(person === NOBODY ? null : NOBODY)}>
              No crew yet
            </Chip>
            <Chip pressed={showOff} onClick={() => setShowOff(!showOff)}>
              Show skipped and canceled
            </Chip>
          </div>
        )}
        <div className="segmented" role="tablist" aria-label="View">
          {(
            [
              ...(routing ? ([["route", "Route", "route"]] as const) : []),
              ["list", "List", "list"],
              ["month", "Month", "month"],
            ] as const
          ).map(([v, label, icon]) => (
            <button key={v} type="button" role="tab" aria-selected={shownView === v} onClick={() => setView(v)}>
              <Icon name={icon} size={16} />
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="sched-body">
        {shownView === "route" ? (
          <RouteDay date={anchor} onDate={setAnchor} />
        ) : visits.error ? (
          <LoadError error={visits.error} retry={() => void visits.refetch()} />
        ) : !data ? (
          <Skeleton rows={6} />
        ) : shownView === "list" ? (
          <div className="sched-dayboard">
            <div className="psched-strip" role="group" aria-label="Days of the week">
              {days.map((d) => {
                const count = byDay(d).length;
                return (
                  <button
                    key={d}
                    type="button"
                    className={`psched-day${d === anchor ? " sel" : ""}${d === today ? " today" : ""}${over === d ? " over" : ""}`}
                    aria-pressed={d === anchor}
                    aria-label={`${dayDate(d)}, ${plural(count, "visit")}`}
                    onClick={() => setAnchor(d)}
                    {...drop(d, (v) => moveTo(v, d))}
                  >
                    <span>{weekdayName(d).slice(0, 3)}</span>
                    <b className="num">{Number(d.slice(8))}</b>
                    <small className="num">{count ? plural(count, "visit") : "None"}</small>
                  </button>
                );
              })}
            </div>
            {walkLines(anchor).length ? <div className="sched-walks">{walkLines(anchor)}</div> : null}
            <DayBoard
              day={byDay(anchor)}
              people={person === NOBODY ? [] : person ? people.filter((p) => p.id === person) : busyPeople(people, list)}
              today={today}
              refreshing={visits.isPlaceholderData}
              onPick={(v, at) => setPeek({ v, at })}
              card={(v) => ({ onContextMenu: context(v), ...drag(v) })}
            />
          </div>
        ) : (
          <div
            className={`sched-month${visits.isPlaceholderData ? " refreshing" : ""}`}
            style={{ ["--weeks" as string]: days.length / 7 }}
          >
            {days.slice(0, 7).map((d) => (
              <span key={d} className="sched-month-head" aria-hidden>
                {weekdayName(d).slice(0, 3)}
              </span>
            ))}
            {days.map((d) => {
              const here = byDay(d);
              return (
                <section
                  key={d}
                  className={`sched-day sched-cell${d === today ? " today" : ""}${over === d ? " over" : ""}${d < monthStart || d > monthEnd ? " outside" : ""}`}
                  {...drop(d, (v) => moveTo(v, d))}
                  aria-label={`${dayDate(d)}, ${plural(here.length, "visit")}`}
                >
                  <header>
                    <button
                      type="button"
                      className="sched-date num"
                      onClick={() => openDay(d)}
                      title={`Open the week of ${dayDate(d)}`}
                    >
                      {Number(d.slice(8))}
                    </button>
                    {here.length ? <span className="sched-count">{plural(here.length, "visit")}</span> : null}
                    {add(d)}
                  </header>
                  <DayStack lines={items(d)} />
                </section>
              );
            })}
          </div>
        )}
      </div>
      {shownView === "route" ? null : (
        <div className="sched-legend" aria-label="Colors">
          {routeList
            .filter((r) => r.active)
            .map((r) => (
              <span key={r.id} className="sched-key sched-route-key" style={{ ["--rc" as string]: routeById.get(r.id)?.color }}>
                <span className="kdot" aria-hidden />
                {r.name}, {WEEKDAYS[r.weekday - 1]!.slice(0, 3)}
              </span>
            ))}
          {KIND_WORDS.map(([k, label]) => (
            <span key={k} className={`sched-key kind-${k}`}>
              <span className="kdot" aria-hidden />
              {label}
            </span>
          ))}
          <span className="sched-key done">
            <Icon name="check" size={12} />
            Done
          </span>
          {movable && list.length ? (
            <span className="sched-hint">
              {routeList.length
                ? "Drag a visit or a route to move it. Click a route for its stops. Right-click a visit for more."
                : "Drag a visit to move it. Right-click for more."}
            </span>
          ) : null}
        </div>
      )}
      {routeMove ? (
        <MoveRouteModal route={routeMove.route} from={routeMove.from} to={routeMove.to} onClose={() => setRouteMove(null)} />
      ) : null}
      {menu ? (
        <div className="menu context-menu" role="menu" style={{ left: menu.x, top: menu.y }} onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setMenu(null);
              open(menu.v);
            }}
          >
            Open
          </button>
          {movable && menu.v.status !== "done" ? (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setEditing(menu.v);
                setMenu(null);
              }}
            >
              Edit schedule
            </button>
          ) : null}
          {can("visit.update") ? (
            menu.v.status === "done" ? (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  const v = menu.v;
                  setMenu(null);
                  reopen.mutate(v.ref, {
                    onSuccess: () => toast("Visit reopened"),
                    onError: (e) => toast(errText(e), { kind: "error" }),
                  });
                }}
              >
                Reopen
              </button>
            ) : (
              <>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    const v = menu.v;
                    setMenu(null);
                    complete.mutate(
                      { ref: v.ref },
                      {
                        onSuccess: (d) => toast(`${v.clientName} marked done`, { undo: () => reopen.mutate(d.ref) }),
                        onError: (e) => toast(errText(e), { kind: "error" }),
                      },
                    );
                  }}
                >
                  Complete
                </button>
                {menu.v.status === "skipped" ? (
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      const v = menu.v;
                      setMenu(null);
                      status.mutate(
                        { ref: v.ref, status: "scheduled" },
                        { onError: (e) => toast(errText(e), { kind: "error" }) },
                      );
                    }}
                  >
                    Put back on
                  </button>
                ) : (
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      const v = menu.v;
                      setMenu(null);
                      status.mutate(
                        { ref: v.ref, status: "skipped" },
                        {
                          onSuccess: (d) =>
                            toast(`Skipped ${shortDate(v.date)}`, {
                              undo: () => status.mutate({ ref: d.ref, status: "scheduled" }),
                            }),
                          onError: (e) => toast(errText(e), { kind: "error" }),
                        },
                      );
                    }}
                  >
                    Skip this visit
                  </button>
                )}
              </>
            )
          ) : null}
        </div>
      ) : null}
      {peek ? (
        <VisitPeek
          visit={peek.v}
          at={peek.at}
          today={today}
          onClose={() => setPeek(null)}
          onEdit={(v) => {
            setPeek(null);
            setEditing(v);
          }}
          onDetails={(v) => {
            setPeek(null);
            setDrawer(v.ref);
          }}
        />
      ) : null}
      {drawer ? <VisitDrawer visitRef={drawer} onClose={() => setDrawer(null)} /> : null}
      {editing ? <ScheduleSheet open onClose={() => setEditing(null)} visit={editing} /> : null}
    </Page>
  );
}
